import isEqual from 'lodash/isEqual';
import lowerCase from 'lodash/lowerCase';

import {
  DISMISSIBLE_BANNER,
  DISMISSIBLE_DISCLOSURE_BANNER,
  NON_DISMISSIBLE_BANNER,
} from '@dmi/shared/redux/Main/constants';

import AsyncStorage from '../../utils/AsyncStorage';
import KeychainStore from '../../utils/KeychainStore';
import StackActions from '../../utils/StackActions';
import {
  ACCOUNT_LOCKOUT_EXPIRATION_TIME,
  WHATS_NEW_TILES_DICTIONARY as DICT,
  LINK_CLIENT_ATTEMPT_COUNT,
  LINK_CLIENT_EXPIRATION_TIME,
  PIN_ATTEMPT_COUNT,
  PIN_LOCKOUT_EXPIRATION_TIME,
} from './constants';

/**
 * @description If the user has a pin or touch id, make sure the user is the same, also
 *              check if the username key has been corrupted/thrown error.
 * @async
 * @param {string} - A string containing the current users username.
 * @return {Object} Object containing isNewUser(Boolean) and usernameKeychain(string).
 */
export async function checkIfIsNewUser(username) {
  const usernameKeychain = await KeychainStore.getUsername();
  const isNewUser = (!usernameKeychain ||
    !isEqual(lowerCase(usernameKeychain), lowerCase(username))
  );
  return { isNewUser, usernameKeychain };
}

/**
 * @description determines whether to use the push or replace method from the
 *              platorm specific navigation object
 * @returns UI navigation function
 */
export const getNavHandler = ({ isLoginRequest, isMobile, navigationObj }) => {
  const navAction = isLoginRequest ? 'push' : 'replace';

  return (prop) => isMobile ?
    navigationObj.current.dispatch(StackActions[navAction](prop)) :
    navigationObj[navAction](prop);
};

// ***** PIN functions that track/handle attempt count and lockout timer ***** //

export const getPinAttemptCount = async () => Number(await AsyncStorage.getItem(PIN_ATTEMPT_COUNT));

export const getClientLinkAttemptCount = async () =>
  Number(await AsyncStorage.getItem(LINK_CLIENT_ATTEMPT_COUNT));

/**
 * @param {'pin'|'account'} type
 * @returns date/time lockout will expire (ms)
 */
export const getLockoutExpirationTime = async (type) => {
  if (type === 'pin') {
    return Number(await AsyncStorage.getItem(PIN_LOCKOUT_EXPIRATION_TIME));
  }
  if (type === 'clientLink') {
    return Number(await AsyncStorage.getItem(LINK_CLIENT_EXPIRATION_TIME));
  }
  return Number(await AsyncStorage.getItem(ACCOUNT_LOCKOUT_EXPIRATION_TIME));
};

export const incrementPinAttemptCount = async () => {
  const currentCount = await getPinAttemptCount() || 0;
  const newCount = currentCount + 1;
  await AsyncStorage.setItem(PIN_ATTEMPT_COUNT, String(newCount));
  return newCount;
};

export const incrementClientLinkAttemptCount = async () => {
  const currentCount = await getClientLinkAttemptCount() || 0;
  const newCount = currentCount + 1;
  await AsyncStorage.setItem(LINK_CLIENT_ATTEMPT_COUNT, String(newCount));
  return newCount;
};

/**
 * @param {'pin'|'account'} type
 * @returns date/time lockout will expire (format: ms since UNIX epoch)
 */
export const setLockoutExpirationTime = async (type) => {
  const expirationTime = Date.now() + (1000 * 60 * 15); // 15 minutes in future
  let storageKeyName;
  if (type === 'pin') {
    storageKeyName = PIN_LOCKOUT_EXPIRATION_TIME;
  } else if (type === 'clientLink') {
    storageKeyName = LINK_CLIENT_EXPIRATION_TIME;
  } else {
    storageKeyName = ACCOUNT_LOCKOUT_EXPIRATION_TIME;
  }
  await AsyncStorage.setItem(storageKeyName, String(expirationTime));
  return expirationTime;
};

/**
 * @param {'pin'|'account'} type
 */
export const getIsLockedOut = async (type) => {
  const expirationTime = await getLockoutExpirationTime(type);
  if (expirationTime && expirationTime > Date.now()) {
    return true;
  }
  return false;
};

/**
 * Removes given type's expiration time from AsyncStorage. If no type is provided, removes all
 * expiration times.
 * @param {'pin'|'account'} type
 */
export const removeLockoutExpirationTime = async (type) => {
  if (type === 'pin') {
    await AsyncStorage.removeItem(PIN_LOCKOUT_EXPIRATION_TIME);
  } else if (type === 'clientLink') {
    await AsyncStorage.removeItem(LINK_CLIENT_EXPIRATION_TIME);
  } else if (type === 'account') {
    await AsyncStorage.removeItem(ACCOUNT_LOCKOUT_EXPIRATION_TIME);
  } else {
    await Promise.all([
      AsyncStorage.removeItem(PIN_LOCKOUT_EXPIRATION_TIME),
      AsyncStorage.removeItem(ACCOUNT_LOCKOUT_EXPIRATION_TIME),
    ]);
  }
};

export const removePinAttemptCount = async () => (
  AsyncStorage.removeItem(PIN_ATTEMPT_COUNT)
);

export const removeClientLinkAttemptCount = async () => (
  AsyncStorage.removeItem(LINK_CLIENT_ATTEMPT_COUNT)
);

/**
 * Increments pinAttemptCount tracker, checks if it has reached the limit. If so, sets
 * lockout timer (and clears pinAttemptCount). Returns object saying whether lockout
 * got triggered and an appropriate error message.
 */
export const handlePinFailure = async () => {
  const pinAttemptCount = await incrementPinAttemptCount();
  if (pinAttemptCount >= 5) {
    await setLockoutExpirationTime('pin');
    await removePinAttemptCount();
    return {
      errorMessage: 'You entered an incorrect PIN five times. Please try again later.',
      isLockedOut: true,
    };
  }
  return {
    errorMessage: 'You entered an incorrect PIN. Please try again.',
    isLockedOut: false,
  };
};

/**
 * Increments clientLinkAttemptCount tracker, checks if it has reached the limit. If so, sets
 * lockout timer (and clears attempt count). Returns object saying whether lockout
 * got triggered and an appropriate error message.
 */
export const handleClientLinkFailure = async () => {
  const attemptCount = await incrementClientLinkAttemptCount();
  if (attemptCount >= 5) {
    await setLockoutExpirationTime('clientLink');
    await removeClientLinkAttemptCount();
  }
};

/**
 * Sets new account lockout expiration time, but only if the account isn't already locked out.
 * (Also, if sets new expiration time, resets pinAttemptCount.)
 */
export const handleAccountLockout = async () => {
  const isAccountAlreadyLockedOut = await getIsLockedOut('account');
  if (!isAccountAlreadyLockedOut) {
    await setLockoutExpirationTime('account');
    /**
     * If the user had failed 4 times with a PIN (where 5 triggers a lockout), then tried UN/PW
     * enough times to trigger account lockout; after that timer is done, a single PIN failure
     * shouldn't trigger a whole new lockout timer. So we reset the pinAttemptCount here.
     */
    await removePinAttemptCount();
  }
};

export const marketingTilesPropsHelper = ({
  isEligibleForAutopay,
  isEligibleForPaperlessBilling,
  isMurabahaAccount,
}) => {
  if (!isEligibleForAutopay && isEligibleForPaperlessBilling) {
    return [
      { ...DICT.paperlessBilling },
      isMurabahaAccount ? { ...DICT.documents } : { ...DICT.learningCenter },
      { ...DICT.amortization },
    ];
  }
  if (isEligibleForAutopay && !isEligibleForPaperlessBilling) {
    return [
      { ...DICT.autopay },
      isMurabahaAccount ? { ...DICT.documents } : { ...DICT.learningCenter },
      { ...DICT.amortization },
    ];
  }
  if (isEligibleForAutopay && isEligibleForPaperlessBilling) {
    return [
      { ...DICT.autopay },
      { ...DICT.paperlessBilling },
      { ...DICT.amortization },
    ];
  }
  return [
    { ...DICT.settings },
    { ...DICT.amortization },
    { ...DICT.documents },
  ];
};

export const getDismissedDocUploadStatusBanners = async () => {
  try {
    const result = await AsyncStorage.getItem('dismissedDocUploadStatusBanners');
    if (!result) return [];
    const dismissedDocUploadNotificationFailures = JSON.parse(result);
    return dismissedDocUploadNotificationFailures;
  } catch {
    return [];
  }
};

/**
 * @description fetches the dismissedBanners array from platform specific storage and returns it.
 *              If key doesn't exist, or an error retrieving, returns an empty array.
 * @return {array} array of banner names that have been dismissed
 */
export const getDismissedPreLoginBanners = async () => {
  try {
    const result = await AsyncStorage.getItem('dismissedBanners');
    if (!result) return [];
    const dismissedBanners = JSON.parse(result);
    return dismissedBanners;
  } catch {
    return [];
  }
};

export const setDismissedDocUploadStatusBanner = async (documentId) => {
  const result = await AsyncStorage.getItem('dismissedDocUploadStatusBanners');
  if (!result) {
    await AsyncStorage.setItem('dismissedDocUploadStatusBanners', JSON.stringify([documentId]));
  } else {
    const dismissedDocUploadNotificationFailures = JSON.parse(result);
    dismissedDocUploadNotificationFailures.push(documentId);
    await AsyncStorage.setItem(
      'dismissedDocUploadStatusBanners',
      JSON.stringify(dismissedDocUploadNotificationFailures),
    );
  }
};

/**
 * @description Given a bannerName, fetches the dismissedBanners array from platform specific
 *              storage and adds the bannerName to the array. If the array doesn't exist,
 *              it creates it.
 * @param {string} bannerName the name of the banner to be dismissed
 * @return {void}
 */
export const setDismissedPreLoginBanner = async (bannerName) => {
  const result = await AsyncStorage.getItem('dismissedBanners');
  if (!result) {
    await AsyncStorage.setItem('dismissedBanners', JSON.stringify([bannerName]));
  } else {
    const dismissedBanners = JSON.parse(result);
    dismissedBanners.push(bannerName);
    await AsyncStorage.setItem('dismissedBanners', JSON.stringify(dismissedBanners));
  }
};

/**
 * @description Given an array of banner objects to be rendered,
 *              returns an object of sorted banner object arrays, keys
 *              corresponding to render location
 * @param {array} banners array of banner objects
 * @returns {object} object consisting of topBanners and bottomBanners banner object arrays:
 *              {
 *                topBanners: [{ sorted bannner objects }],
 *                bottomBanners: [{ sorted banner objects }]
 *               }
 */
export const getTopAndBottomPreLoginBanners = (banners) => {
  const bannerTypes = {
    dismissible: [DISMISSIBLE_BANNER, DISMISSIBLE_DISCLOSURE_BANNER],
    nonDismissible: [NON_DISMISSIBLE_BANNER],
  };
  if (!banners.length) return { bottomBanners: [], topBanners: [] };
  const result = banners.reduce(
    (acc, banner) => {
      const { bannerType } = banner || {};
      if (bannerTypes.nonDismissible.includes(bannerType)) {
        acc.nonDismissibleBanners.push(banner);
      } else if (bannerTypes.dismissible.includes(bannerType)) {
        if (bannerType === DISMISSIBLE_BANNER) {
          acc.dismissibleBanners.unshift(banner);
        } else {
          acc.dismissibleBanners.push(banner);
        }
      }
      return acc;
    },
    { dismissibleBanners: [], nonDismissibleBanners: [] },
  );
  const { dismissibleBanners, nonDismissibleBanners } = result;

  // sort dismissable banners by date added - newest first
  dismissibleBanners.sort((a, b) => b.dateAdded - a.dateAdded);
  const topBanners =
    nonDismissibleBanners.length > 0 ? nonDismissibleBanners : [dismissibleBanners[0]];

  const bottomBanners = dismissibleBanners.filter(
    (banner) => !topBanners.includes(banner),
  );

  return { bottomBanners, topBanners };
};

/**
 * @description checks if given item keys are in async storage, and if so removes them
 * @param {array} keys an array of item keys
 */
export const removeItemsFromAsyncStorage = async (keys) => {
  keys.forEach(async (key) => {
    const value = await AsyncStorage.getItem(key);
    if (value) {
      await AsyncStorage.removeItem(key);
    }
  });
};

/**
 * @description sorts an array of data based on inactive items & primary loan status
 * @param {array} data an array of address or loan objects
 * @return {array} sorted array of data objects with inactive loans sorted to the end and primary
 * loan (if active) sorted to the beginning
 */
export const sortLoansByOnlineAvailabilityStatus = (loans) => {
  let activeLoans = [];
  let inactiveLoans = [];

  loans.forEach((loan) => {
    const { isPrimaryLoan, onlineAvailabilityStatus } = loan;
    const isLoanAvailable = onlineAvailabilityStatus === 'Available';

    if (isPrimaryLoan && isLoanAvailable) {
      activeLoans = [loan, ...activeLoans];
    } else if (isLoanAvailable) {
      activeLoans = [...activeLoans, loan];
    } else {
      inactiveLoans = [...inactiveLoans, loan];
    }
  });
  return activeLoans.concat(inactiveLoans);
};
